class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // `?.` no método: no fallback sem StandardEvents a base não tem dispatchViewEvent.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ============================================================
   Boneca de Pano — extensões do cart drawer
   1) bdpTraduzErroCupom — port de src/lib/shopify-errors.ts.
   2) <bdp-coupon-form> — cupom via Cart AJAX nativo (/cart/update
      com { discount }), re-renderizando as sections 'cart-drawer'
      e 'cart-icon-bubble' pedidas no próprio POST.
   3) Ponte pubsub -> document: re-emite o 'cart-update' interno do
      Dawn como CustomEvent 'cart:update' no document. Cobre TODA
      mutação (add via product-form, qty/remover via cart-items,
      cupom, quick-add, Standard Actions) — o bottom-nav escuta.
   ============================================================ */

function bdpTraduzErroCupom(mensagem) {
  if (!mensagem) return 'Cupom inválido. Verifique e tente novamente.';
  const lower = String(mensagem).toLowerCase();

  if (lower.includes('not found') || lower.includes('invalid') || lower.includes('does not exist')) {
    return 'Cupom não encontrado.';
  }
  if (lower.includes('expired')) return 'Cupom expirado.';
  if (lower.includes('usage limit') || lower.includes('already used') || lower.includes('limit reached')) {
    return 'Cupom já utilizado ou esgotado.';
  }
  if (lower.includes('minimum') || lower.includes('subtotal')) {
    return 'Valor mínimo do pedido não atingido.';
  }
  if (lower.includes('not applicable') || lower.includes('cannot be applied')) {
    return 'Cupom não aplicável aos itens do carrinho.';
  }
  return 'Cupom inválido. Verifique e tente novamente.';
}

if (!customElements.get('bdp-coupon-form')) {
  customElements.define(
    'bdp-coupon-form',
    class BdpCouponForm extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.bdp-coupon__toggle');
        this.panel = this.querySelector('.bdp-coupon__panel');
        this.input = this.querySelector('.bdp-coupon__input');
        this.applyButton = this.querySelector('.bdp-coupon__apply');
        this.removeButton = this.querySelector('.bdp-coupon__remove');
        this.errorElement = this.querySelector('.bdp-coupon__error');

        // O input do cupom vive dentro de <cart-drawer-items>, que escuta
        // 'change' pra validar quantidade de linha — o change do cupom
        // não pode borbulhar até lá.
        this.addEventListener('change', (event) => event.stopPropagation());

        this.toggle?.addEventListener('click', this.onToggle.bind(this));
        this.applyButton?.addEventListener('click', this.onApply.bind(this));
        this.removeButton?.addEventListener('click', () => this.updateDiscount(''));
        this.input?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.onApply();
        });
        this.input?.addEventListener('input', () => {
          this.input.value = this.input.value.toUpperCase();
          this.showError('');
        });
      }

      onToggle() {
        const isOpen = !this.panel.hasAttribute('hidden');
        this.panel.toggleAttribute('hidden', isOpen);
        this.toggle.setAttribute('aria-expanded', String(!isOpen));
        const icon = this.toggle.querySelector('.bdp-coupon__toggle-icon');
        if (icon) icon.textContent = isOpen ? '+' : '−';
        if (!isOpen) this.input?.focus();
      }

      onApply() {
        const code = (this.input?.value || '').trim().toUpperCase();
        if (!code || this.loading) return;
        this.updateDiscount(code);
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.toggleAttribute('hidden', message === '');
      }

      setLoading(loading) {
        this.loading = loading;
        [this.applyButton, this.removeButton].forEach((button) => {
          if (button) button.disabled = loading;
        });
        if (this.applyButton) this.applyButton.textContent = loading ? '...' : 'Aplicar';
      }

      updateDiscount(code) {
        this.showError('');
        this.setLoading(true);

        const config = fetchConfig();
        config.body = JSON.stringify({
          discount: code,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, config)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || cart.status || cart.errors) {
              this.setLoading(false);
              this.showError(bdpTraduzErroCupom(cart && (cart.message || cart.description || cart.errors)));
              return;
            }

            // Se a resposta expõe discount_codes (Cart AJAX atual), valida o
            // código: ausente ou applicable === false -> rejeitado. Se o campo
            // não existir, segue o fluxo e confia no re-render das sections.
            if (code !== '' && Array.isArray(cart.discount_codes)) {
              const entry = cart.discount_codes.find(
                (discount) => (discount.code || '').toUpperCase() === code
              );
              const applied = Boolean(entry) && entry.applicable !== false;
              if (!applied) {
                // Limpa o código rejeitado do cart pra não sujar o checkout.
                if (entry) {
                  const cleanup = fetchConfig();
                  cleanup.body = JSON.stringify({ discount: '' });
                  fetch(`${routes.cart_update_url}`, cleanup).catch(() => {});
                }
                this.setLoading(false);
                this.showError('Cupom inválido ou não aplicável aos itens do carrinho.');
                return;
              }
            }

            // Re-render: tier bar, itens, savings, upsell e footer moram todos
            // dentro da section cart-drawer — o replace cobre tudo de uma vez.
            this.renderSections(cart);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bdp-coupon-form', cartData: cart });
          })
          .catch(() => {
            this.setLoading(false);
            this.showError('Não foi possível aplicar o cupom. Tente novamente.');
          });
      }

      renderSections(cart) {
        const sections = (cart && cart.sections) || {};

        const drawerHtml = sections['cart-drawer'];
        const drawerTarget = document.getElementById('CartDrawer');
        if (drawerHtml && drawerTarget) {
          const source = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          if (source) {
            drawerTarget.innerHTML = source.innerHTML;
            const drawer = document.querySelector('cart-drawer');
            if (drawer) {
              drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
            }
          }
        }

        const bubbleHtml = sections['cart-icon-bubble'];
        const bubbleTarget = document.getElementById('cart-icon-bubble');
        if (bubbleHtml && bubbleTarget) {
          const source = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
          if (source) bubbleTarget.innerHTML = source.innerHTML;
        }
      }
    }
  );
}

if (!window.bdpCartUpdateBridge) {
  window.bdpCartUpdateBridge = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        detail: {
          source: (event && event.source) || 'unknown',
          cart: (event && event.cartData) || null,
        },
      })
    );
  });
}
